import {
  Avatar,
  ClientLink,
  Confirmation,
  DateTime,
  InternalClientTooltip,
  MemberContactPopover,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  Tooltip,
} from '~/components';
import { useApi, useConfirmation, useToast, useSession, useWorkspace } from '~/contexts';
import React from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '~/styles';
import ClientDeleteConfirmation from './ClientDeleteConfirmation';
import ClientCollapseConfirmation from './ClientCollapseConfirmation';

const ClientName = styled.span`
  display: flex;
  color: ${colors.black};
`;

const Owner = styled.div`
  display: flex;
  align-items: center;
`;

const OwnerInfo = styled.span`
  margin-left: 0.5rem;
`;

function ClientRow({ client, selection, onSaved, onDeleted, onCollapse }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();

  const { url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const { isAdmin: isSysAdmin } = useSession();
  const confirmation = useConfirmation();

  const handleDelete = () => {
    confirmation.prompt((resolve) => (
      <ClientDeleteConfirmation
        client={client}
        onClose={resolve}
        onDelete={() => {
          history.push({ pathname: `/app/${workspace.key}/clients`, search: location.search });
          if (onDeleted) onDeleted(client.id);
          resolve(true);
        }}
      />
    ));
  };

  const handleCollapse = () => {
    confirmation.prompt((resolve) => (
      <ClientCollapseConfirmation
        client={client}
        onClose={resolve}
        onCollapse={() => {
          history.push({ pathname: `/app/${workspace.key}/clients`, search: location.search });
          if (onCollapse) onCollapse(client.id);
          resolve(true);
        }}
      />
    ));
  };

  async function handleSetRecordStatus(recordStatusId) {
    try {
      const { data } = await api.www.workspaces(workspace.id).clients(client.id).setRecordStatus({ recordStatusId });
      await onSaved(data);
    } catch ({ message }) {
      toast.error(message);
    }
  }

  const clientUrl = `${url}/${client.key}`;

  const handleClientStatement = (client) => {
    history.push(`/app/${workspace.key}/reports/client-statement?client=${client.id}&currency=${client.currency}`);
  };

  const handleEdit = (client) => history.push({ pathname: `${url}/edit/${client.key}`, search: location.search });

  const handleView = () => history.push(clientUrl);

  return (
    <ListView.Row isDisabled={client.recordStatus.id === 'archived'} onClick={handleView}>
      {selection && <ListViewSelection.Cell id={client.id} selection={selection} />}
      <ListView.Cell>
        <ClientName>
          <ClientLink client={client} onClick={(e) => e.stopPropagation()} />
          {client.isInternal && <InternalClientTooltip />}
        </ClientName>
      </ListView.Cell>
      <ListView.Cell style={{ wordBreak: 'break-word' }}>{client.code}</ListView.Cell>
      <ListView.Cell>
        {client.owner && (
          <Owner>
            <MemberContactPopover member={client.owner} placement="left">
              <Avatar value={client.owner} isCircle hasBackground initialsFontSize=".9rem" />
            </MemberContactPopover>
            <OwnerInfo>{client.owner.name}</OwnerInfo>
          </Owner>
        )}
      </ListView.Cell>
      <ListView.Cell>
        {client.salesRepresentative && (
          <Owner>
            <MemberContactPopover member={client.salesRepresentative} placement="left">
              <Avatar value={client.salesRepresentative} isCircle hasBackground initialsFontSize=".9rem" />
            </MemberContactPopover>
            <OwnerInfo>{client.salesRepresentative.name}</OwnerInfo>
          </Owner>
        )}
      </ListView.Cell>
      <ListView.Cell>{client.industry?.name}</ListView.Cell>
      <ListView.Cell>{client.practice?.name}</ListView.Cell>
      <ListView.Cell>{client.businessUnit?.name}</ListView.Cell>
      <ListView.Cell>{client.location?.name}</ListView.Cell>
      <ListView.Cell>{client.currency}</ListView.Cell>
      <ListView.Cell>
        <DateTime value={client.createdAt} />
      </ListView.Cell>
      <ListViewActions>
        <ListViewActions.View onClick={handleView} />

        <hr />

        <ListViewMenu>
          {({ setIsOpen }) => {
            const handleAction = (action) => setIsOpen(false) || action;

            const handleArchive = async () => {
              setIsOpen(false);

              const confirm = await confirmation.prompt((resolve) => (
                <Confirmation resolve={resolve}>
                  Are you sure that you want to archive this client? This will also archive all projects within the
                  client.
                </Confirmation>
              ));

              if (!confirm) return;

              handleSetRecordStatus('archived');
            };

            const handleUnarchive = async () => {
              setIsOpen(false);

              const confirm = await confirmation.prompt((resolve) => (
                <Confirmation resolve={resolve}>
                  This action will unarchive the client. Note that this will not unarchive any projects within the
                  client. Projects must be unarchived separately.
                </Confirmation>
              ));

              if (!confirm) return;

              handleSetRecordStatus('active');
            };

            return (
              <>
                <ListViewMenu.Item onClick={() => handleAction(handleView())}>View</ListViewMenu.Item>

                <ListViewMenu.Item
                  disabled={!client.permissions.edit}
                  tooltip={!client.permissions.edit ? 'Insufficient permissions to edit this client.' : undefined}
                  onClick={() => handleEdit(client)}>
                  Edit
                </ListViewMenu.Item>

                {client.recordStatus.id === 'active' ? (
                  <ListViewMenu.Item
                    disabled={!client.permissions.delete || client.isInternal}
                    tooltip={
                      client.isInternal
                        ? `The workspace's internal client cannot be archived.`
                        : !client.permissions.delete
                          ? 'Insufficient permissions to archive this client.'
                          : undefined
                    }
                    onClick={handleArchive}>
                    Archive
                  </ListViewMenu.Item>
                ) : (
                  <ListViewMenu.Item
                    disabled={!client.permissions.delete}
                    tooltip={
                      !client.permissions.delete ? 'Insufficient permissions to unarchive this client.' : undefined
                    }
                    onClick={handleUnarchive}>
                    Unarchive
                  </ListViewMenu.Item>
                )}

                {client.permissions.viewPublishedInvoices && !client.isInternal && (
                  <ListViewMenu.Item onClick={() => handleClientStatement(client)}>
                    View Client Statement
                  </ListViewMenu.Item>
                )}

                <ListViewMenu.Item
                  disabled={client.isInternal || !client.permissions.delete}
                  tooltip={
                    client.isInternal
                      ? `The workspace's internal client cannot be deleted.`
                      : !client.permissions.delete
                        ? 'Insufficient permissions to delete this client.'
                        : undefined
                  }
                  onClick={handleDelete}>
                  Delete
                </ListViewMenu.Item>
                {isSysAdmin && (
                  <Tooltip message={'Merge this client and its projects into another client.'}>
                    <ListViewMenu.Item
                      disabled={!client.permissions.edit}
                      tooltip={!client.permissions.edit ? 'Insufficient permissions to edit this client.' : undefined}
                      onClick={() => handleCollapse(client)}>
                      Merge
                    </ListViewMenu.Item>
                  </Tooltip>
                )}
              </>
            );
          }}
        </ListViewMenu>
      </ListViewActions>
    </ListView.Row>
  );
}

export default ClientRow;
